/* eslint-disable max-len */
import { moduleClientFactory } from '../../api-service/module-client-factory';
import {
  assignmentPathName,
  authorityAreaPathName,
  benefitExtensionPathName,
  citizenAreaPathName,
  citizenAvailabilityTabPathName,
  citizenDetailsTabPathName,
  citizenInterviewBookingPath,
  citizenInterviewPath,
  citizenJournalNote,
  citizenNuphForm,
  citizenPersonContactPathNameWithoutId,
  cvJobsearchTabPathName,
  jobReferralDetailPath,
  journalNotePathName,
  journalNoteView,
  journalTabPathName,
  missingParticipationPathName,
  partialRecoveryPathName,
  planAndActivitiesContractEditPathName,
  planAndActivitiesTabPathName,
  planAndActivitiesView,
  sicknessBenefitVisitationPathName,
  unemploymentTabPathName,
} from '../../areas/citizen/authority/citizen-authority-area-pathnames';
import { interventionObjectivePathName } from '../../areas/citizen/common/intervention-objective/intervention-objective-pathnames';
import {
  activityCoPaymentEditPathName,
  activityReimbursementEditPathName,
  associatedCaseWorkerGuidePathname,
  caseGuidePathname,
  offerAndActivityAreaPathName,
  viewActivityPathName,
  viewEvaluationPathName,
  viewOfferPathName,
  waitingListSeatPathName,
  waitingListSeatEditPathName,
} from '../../areas/offer-and-activity/offer-and-activity-area-pathnames';
import { formatCprNoDash, joinURL } from '../../utilities';
import { redirectToTask } from './task-redirect';
import {
  companyRecruitmentsPathName,
  companyRecruitmentDetailsPathName,
  companyRecruitmentDetailsFromCandidatePathName,
  effortsAreaPathName,
  jobAdsPathName,
  jobAdUniformPathName,
} from '../../areas/efforts/efforts-area-pathnames';
import { companyNoteEditPathName } from '../../areas/company/company-area-pathnames';
import { attendanceAreaPathName, getEducatorEditPath, getLessonProtocolPath } from '../../areas/attendance/attendance-area-pathnames';
import {
  interventionObjectiveStatusPathName,
  serviceProviderAreaPathName,
  viewAssignmentPathName,
} from '../../areas/citizen/serviceprovider/citizen-serviceprovider-area-pathnames';

export async function createUrlToActualResource(
  resourceName: string,
  resourceId: string,
  jdcBaseUrl: string,
  searchParams?: URLSearchParams,
  rootEntityId?: string,
  socialProviderCitizenLandingTab: string = citizenDetailsTabPathName,
  isOnlyServiceProvider = false,
  isOnlyMedicalCollaborator = false
): Promise<string | null> {
  function citizenLandingUrl(citizenId: string) {
    if (isOnlyServiceProvider) {
      const baseUrl = joinURL(jdcBaseUrl, citizenAreaPathName, serviceProviderAreaPathName, citizenId, socialProviderCitizenLandingTab);
      const spuId = searchParams?.get('spuid');
      return spuId ? `${baseUrl}?spuid=${spuId}` : baseUrl;
    }
    return joinURL(jdcBaseUrl, citizenAreaPathName, authorityAreaPathName, citizenId, citizenDetailsTabPathName);
  }

  switch (resourceName.toLowerCase()) {
    case 'si_interview':
      return joinURL(jdcBaseUrl, citizenInterviewPath, resourceId);
    case 'si_journalnote': {
      const citizenId = searchParams?.get('citizenId');
      if (!citizenId) {
        return joinURL(jdcBaseUrl, citizenJournalNote, resourceId);
      }
      return joinURL(jdcBaseUrl, 'standalone', citizenAreaPathName, citizenId, journalTabPathName, journalNotePathName, journalNoteView, resourceId);
    }
    case 'si_offer': {
      return joinURL(jdcBaseUrl, offerAndActivityAreaPathName, viewOfferPathName, resourceId);
    }
    case 'assessment': {
      const citizenId = searchParams?.get('citizenId');
      return `/standalone/${citizenAreaPathName}/${citizenId}/availability-assessment/view/${resourceId}`;
    }
    case 'account': {
      return '';
    }
    case 'si_accountnote': {
      return joinURL('/company/routing/company-note', resourceId);
    }
    case 'booking': {
      return joinURL(jdcBaseUrl, citizenInterviewBookingPath, '-', resourceId);
    }
    case 'joblog': {
      // Needs the citizen, since the joblog detail view lives under the citizen's CV & jobsearch tab.
      const citizenId = searchParams?.get('citizenId');
      if (!citizenId) return null;
      return joinURL('/', 'standalone', citizenAreaPathName, citizenId, cvJobsearchTabPathName, 'joblog', resourceId);
    }
    case 'companynote': {
      return joinURL(jdcBaseUrl, companyNoteEditPathName, resourceId);
    }
    case 'si_document': {
      const citizenId = searchParams?.get('citizenId');
      const editMode = searchParams?.get('edit');

      if (editMode === 'true') {
        return joinURL('/', 'standalone', citizenAreaPathName, citizenId || 'ERROR_NO_CITIZEN_ID', journalTabPathName, 'document', 'create', resourceId);
      }

      if (!citizenId) {
        return joinURL('/', 'citizendocument', resourceId);
      }
      const citizenpart = joinURL('/', 'standalone', citizenAreaPathName, citizenId, journalTabPathName);

      return `${citizenpart}?documentId=${resourceId}`;
    }
    case 'si_case': {
      const caseId = resourceId;
      const caseService = moduleClientFactory.createCitizenCaseClient();
      const caseInfo = await caseService.getCaseInfoForLink({ caseId });
      return `/standalone/${citizenAreaPathName}/${caseInfo.citizenId}/${caseGuidePathname}/${caseId}`;
    }
    case 'partialrecoveries': {
      const partialRecoveryId = resourceId;
      const caseService = moduleClientFactory.createCitizenSicknessBenefitPartialRecoveryClient();
      const partialRecovery = await caseService.getPartialRecoveryById({ partialRecoveryId });
      return `/standalone/${citizenAreaPathName}/${partialRecovery.partialRecovery?.citizenId}/case/${partialRecovery.partialRecovery?.caseId}/partial-recovery/${partialRecovery.partialRecovery?.id}`;
    }
    case 'citizendialog': {
      const citizenDialogId = resourceId;
      const citizenId = searchParams?.get('citizenId');
      return `/${citizenAreaPathName}/${citizenId}/citizenmessage/${citizenDialogId}`;
    }
    case 'si_associateuser': {
      const associatedCaseWorkerId = resourceId;
      const caseService = moduleClientFactory.createCitizenCaseClient();
      const associatedInfo = await caseService.getAssociatedCaseWorker({ associatedCaseWorkerId });

      const caseId = associatedInfo?.associatedCaseWorker?.caseId;
      const caseInfo = await caseService.getCaseInfoForLink({ caseId: caseId || '' });
      return `/standalone/${citizenAreaPathName}/${caseInfo.citizenId}/${caseGuidePathname}/${caseId}/${associatedCaseWorkerGuidePathname}/${associatedCaseWorkerId}`;
    }
    case 'si_evaluation': {
      return joinURL(jdcBaseUrl, offerAndActivityAreaPathName, viewActivityPathName, viewEvaluationPathName, resourceId);
    }
    case 'absence': {
      const client = moduleClientFactory.createCitizenAvailabilityClient();
      const response = await client.getAbsenceUrl({ recordId: resourceId });
      return response.absenceUrl ?? '';
    }
    case 'citizen-exemption': {
      if (!rootEntityId) {
        throw new Error('Could not load exemption without citizenId');
      }
      return joinURL(jdcBaseUrl, citizenAreaPathName, authorityAreaPathName, rootEntityId, citizenAvailabilityTabPathName);
    }
    case 'si_reimbursement':
    case 'reimbursement': {
      let categoryId = parseNumberStr(searchParams?.get('categoryId'));
      let activityId = searchParams?.get('activityId');

      if (!categoryId || !activityId) {
        const client = moduleClientFactory.createOfferAndActivityClient();
        const response = await client.getReimbursementsRedirectProperties({ reimbursementId: resourceId });
        categoryId = response.offerCategory;
        activityId = response.activityId;
      }

      if (activityId) {
        return joinURL(jdcBaseUrl, offerAndActivityAreaPathName, viewActivityPathName, activityId, activityReimbursementEditPathName, resourceId);
      }

      return '';
    }
    case 'co-payment': {
      const client = moduleClientFactory.createOfferAndActivityCoPaymentClient();
      const response = await client.getCoPaymentRedirectProperties({ coPaymentId: resourceId });

      if (response.activityId) {
        return joinURL(jdcBaseUrl, offerAndActivityAreaPathName, viewActivityPathName, response.activityId, activityCoPaymentEditPathName, resourceId);
      }

      return '';
    }
    case 'si_integrationcontract': {
      const citizenId = searchParams?.get('citizenId');

      return joinURL(jdcBaseUrl, 'standalone', citizenAreaPathName, citizenId ?? 'NOTFOUND', planAndActivitiesContractEditPathName);
    }
    case 'si_task': {
      return redirectToTask(resourceId);
    }
    case 'partial-recovery': {
      const citizenId = searchParams?.get('citizenId') || '';

      const path = partialRecoveryPathName.replace(':caseId', resourceId.toLocaleLowerCase()).replace(':partialRecoveryId?', '');
      return joinURL(jdcBaseUrl, citizenAreaPathName, citizenId.toLocaleLowerCase(), path);
    }
    case 'sickness-benefit-visitation': {
      // resourceId is the caseId, not the visitation id — the page shows the one visitation belonging to a case.
      const citizenId = searchParams?.get('citizenId');
      if (!citizenId) {
        return null;
      }

      const path = sicknessBenefitVisitationPathName.replace(':caseId', resourceId.toLocaleLowerCase());
      return joinURL(jdcBaseUrl, citizenAreaPathName, authorityAreaPathName, citizenId.toLocaleLowerCase(), path);
    }
    case 'missing-participation': {
      const citizenId = searchParams?.get('citizenId') || '';
      const caseId = resourceId;
      const path = missingParticipationPathName.replace(':caseId', caseId.toLocaleLowerCase()).replace(':missingParticipationId?', '');
      return joinURL(jdcBaseUrl, citizenAreaPathName, citizenId.toLocaleLowerCase(), path);
    }
    case 'missing-participation-from-crm': {
      const client = moduleClientFactory.createCitizenSicknessBenefitMissingParticipationClient();

      const result = await client.getMissingParticipationById({ missingParticipationId: resourceId });

      const citizenId = result?.missingParticipation?.citizenId || '';
      const caseId = result?.missingParticipation?.caseId || '';
      const path = missingParticipationPathName.replace(':caseId', caseId.toLocaleLowerCase()).replace(':missingParticipationId?', resourceId);
      return joinURL(jdcBaseUrl, citizenAreaPathName, citizenId.toLocaleLowerCase(), path);
    }
    case 'contact': {
      return joinURL(jdcBaseUrl, citizenAreaPathName, authorityAreaPathName, resourceId, citizenDetailsTabPathName);
    }
    case 'socialprovidercitizen': {
      const baseUrl = joinURL(jdcBaseUrl, citizenAreaPathName, serviceProviderAreaPathName, resourceId, socialProviderCitizenLandingTab);
      const spuId = searchParams?.get('spuid');
      return spuId ? `${baseUrl}?spuid=${spuId}` : baseUrl;
    }
    // Sygehusadvis-opgaver hænges op på borgeren, ikke på en enkelt advis, så de fører til
    // borgeroverblikket hvor indlæggelsesadvarslen står.
    case 'medical-collaboration-hospital-stay':
    case 'citizen': {
      return citizenLandingUrl(resourceId);
    }
    // A citizen deep link names a cpr; the lookup runs here so the caseworker's own access decides what they see.
    case 'citizen-by-cpr': {
      if (!resourceId) {
        return null;
      }

      const response = await moduleClientFactory.createSearchClient().getCitizenByCpr({ citizenCpr: formatCprNoDash(resourceId) });
      const citizen = response.citizen?.[0];

      return citizen?.documentId ? citizenLandingUrl(citizen.documentId) : null;
    }
    // Dialogintegration knows only the case, but lands on its citizen - as legacy goto/CitizenByCase did.
    case 'citizen-by-case': {
      const caseId = resourceId;
      const caseService = moduleClientFactory.createCitizenCaseClient();
      const caseInfo = await caseService.getCaseInfoForLink({ caseId });
      return caseInfo.citizenId ? citizenLandingUrl(caseInfo.citizenId) : null;
    }
    case 'contact_crm_only': {
      return '';
    }
    case 'si_personcontact': {
      const citizenPersonContactClient = moduleClientFactory.createCitizenMasterDataCitizenPersonContactClient();
      const response = await citizenPersonContactClient.getCitizenIdForCitizenPersonContact({ citizenPersonContactId: resourceId });
      const { citizenId } = response;
      if (citizenId) {
        return joinURL(jdcBaseUrl, citizenAreaPathName, authorityAreaPathName, citizenId, citizenPersonContactPathNameWithoutId, resourceId);
      }
      return '';
    }
    case 'serviceprovider-si_personcontact': {
      const citizenPersonContactClient = moduleClientFactory.createCitizenMasterDataCitizenPersonContactClient();
      const response = await citizenPersonContactClient.getCitizenIdForCitizenPersonContact({ citizenPersonContactId: resourceId });
      const { citizenId } = response;
      if (citizenId) {
        return joinURL(jdcBaseUrl, citizenAreaPathName, serviceProviderAreaPathName, citizenId, citizenPersonContactPathNameWithoutId, resourceId);
      }
      return '';
    }
    case 'si_placement': {
      return joinURL(jdcBaseUrl, offerAndActivityAreaPathName, viewActivityPathName, resourceId);
    }
    case 'si_recruitmentrequestcandidate': {
      return joinURL(jdcBaseUrl, effortsAreaPathName, companyRecruitmentsPathName, companyRecruitmentDetailsPathName, companyRecruitmentDetailsFromCandidatePathName, resourceId);
    }
    case 'si_benefitextensionassessment': {
      const benefitExtensionAssessmentId = resourceId;
      const client = moduleClientFactory.createCitizenSicknessBenefitBenefitExtensionAssessmentClient();
      const response = await client.getBenefitExtensionAssessmentData({ benefitExtensionAssessmentId });
      const caseId = response?.model?.caseId ?? '';
      const citizenId = response?.model?.citizenId ?? '';
      const path = benefitExtensionPathName.replace(':caseId', caseId.toLocaleLowerCase()).replace(':benefitExtensionId?', benefitExtensionAssessmentId);
      return joinURL(jdcBaseUrl, citizenAreaPathName, citizenId.toLocaleLowerCase(), path);
    }
    case 'benefit-extension': {
      let caseId = '';
      let benefitExtensionAssessmentId = '';
      let citizenId = searchParams?.get('citizenId') ?? '';
      const fromCase = searchParams?.get('fromCase') ?? undefined;

      const client = moduleClientFactory.createCitizenSicknessBenefitBenefitExtensionAssessmentClient();

      if (citizenId) {
        caseId = resourceId;
        const response = await client.getOpenBenefitExtensionAssessmentId({ citizenId, caseId });
        benefitExtensionAssessmentId = response?.benefitExtensionAssessmentId ?? '';
        if (!fromCase) {
          benefitExtensionAssessmentId = resourceId;
        }
      } else {
        benefitExtensionAssessmentId = resourceId;
        const response = await client.getBenefitExtensionAssessmentData({ benefitExtensionAssessmentId });
        caseId = response?.model?.caseId ?? '';
        citizenId = response?.model?.citizenId ?? '';
      }

      const path = benefitExtensionPathName.replace(':caseId', caseId.toLocaleLowerCase()).replace(':benefitExtensionId?', benefitExtensionAssessmentId);
      return joinURL(jdcBaseUrl, citizenAreaPathName, citizenId.toLocaleLowerCase(), path);
    }
    case 'si_plan': {
      const citizenId = searchParams?.get('citizenId');
      if (citizenId) {
        return joinURL(jdcBaseUrl, citizenAreaPathName, citizenId, planAndActivitiesTabPathName, planAndActivitiesView);
      }
      throw new Error('Could not load plan without citizenId');
    }
    case 'persongroupmarking': {
      const personGroupMarkingId = resourceId;

      const personGroupService = moduleClientFactory.createPersonGroupClient();
      const response = await personGroupService.getCitizenIdFromPersonGroupMarkingId({ personGroupMarkingId });

      if (response.citizenId) {
        return joinURL(jdcBaseUrl, citizenAreaPathName, response.citizenId, citizenDetailsTabPathName);
      }
      throw new Error('Could not load persongroupmarking without citizenId');
    }
    case 'cases.resumedwork': {
      const resumedWorkId = resourceId;
      const client = moduleClientFactory.createCitizenSicknessBenefitResumedWorkClient();
      const result = await client.getCitizenIdFromResumedWorkId({ resumedWorkId });
      return joinURL(jdcBaseUrl, citizenAreaPathName, result.citizenId, citizenDetailsTabPathName);
    }
    case 'si_jobreferral': {
      const client = moduleClientFactory.createCitizenJobReferralClient();
      const response = await client.getCitizenIdByJobReferralId({ jobReferralId: resourceId });

      if (response?.citizenId) {
        return joinURL(jdcBaseUrl, citizenAreaPathName, response?.citizenId, cvJobsearchTabPathName, jobReferralDetailPath.replace(':id', resourceId));
      }
      throw new Error('Could not load jobreferral without citizenId');
    }
    case 'nuph': {
      const client = moduleClientFactory.createCitizenUnemploymentBenefitClient();
      const response = await client.getCitizenIdByNuphId({ nuphId: resourceId });
      if (response?.citizenId) {
        return joinURL(jdcBaseUrl, citizenAreaPathName, response?.citizenId, unemploymentTabPathName, citizenNuphForm.replace(':id', resourceId));
      }
      throw new Error('Could not load nuph without citizenId');
    }
    case 'nuph-response': {
      const client = moduleClientFactory.createCitizenUnemploymentBenefitClient();
      const response = await client.getCitizenAndNuphIdByNuphResponseId({ nuphResponseId: resourceId });
      if (response?.citizenId && response?.nuphId) {
        return joinURL(jdcBaseUrl, citizenAreaPathName, response.citizenId, unemploymentTabPathName, citizenNuphForm.replace(':id', response.nuphId));
      }
      throw new Error('Could not load nuph without citizenId');
    }
    case 'jobad': {
      const client = moduleClientFactory.createJobAdsClient();
      const response = await client.getExternalJobAdId({ jobAdId: resourceId });
      if (response?.externalJobAdId) {
        return joinURL(jdcBaseUrl, effortsAreaPathName, jobAdsPathName, jobAdUniformPathName, response.externalJobAdId);
      }
      throw new Error('Could not load jobad without externalJobAdId');
    }
    case 'expectedjobresumption': {
      const expectedJobResumptionId = resourceId;
      const expectedJobResumptionClient = moduleClientFactory.createCitizenSicknessBenefitExpectedJobResumptionClient();
      const expectedJobResumption = await expectedJobResumptionClient.getExpectedJobResumptionById({ expectedJobResumptionId });

      return `/standalone/${citizenAreaPathName}/${expectedJobResumption.expectedJobResumption?.citizenId}/case/${expectedJobResumption.expectedJobResumption?.caseId}/expected-job-resumption/${expectedJobResumptionId}`;
    }
    case 'citizen-grant': {
      const result = await moduleClientFactory.createCitizenGrantClient().getCitizenIdByGrantId({ grantId: resourceId });
      return `/standalone/${citizenAreaPathName}/${result.citizenId}/grant?grantId=${resourceId}`;
    }
    case 'medical-collaboration': {
      const [result, reqCode] = await Promise.all([
        moduleClientFactory.createCitizenMedicalCollaborationClient().getCitizenIdByRequestFormId({ requestFormId: resourceId }),
        moduleClientFactory.createCitizenMedicalCollaborationClient().getRequestFormCodeByRequestFormId({ requestFormId: resourceId }),
      ]);
      const medicalCollaborationArea = isOnlyMedicalCollaborator ? `${citizenAreaPathName}/${serviceProviderAreaPathName}` : citizenAreaPathName;
      if (reqCode?.requestFormCode === 'LAE271') {
        return `/${medicalCollaborationArea}/${result.citizenId}/medical-collaboration?medicalId=${resourceId}`;
      }
      return `/${medicalCollaborationArea}/${result.citizenId}/medical-collaboration/medical-request/${resourceId}`;
    }
    case 'medical-collaboration-correspondence': {
      const result = await moduleClientFactory.createCitizenMedicalCollaborationClient().getCitizenIdByCorrespondenceMessageId({ correspondenceMessageId: resourceId });
      const medicalCollaborationArea = isOnlyMedicalCollaborator ? `${citizenAreaPathName}/${serviceProviderAreaPathName}` : citizenAreaPathName;
      return `/${medicalCollaborationArea}/${result.citizenId}/medical-collaboration/correspondence/${resourceId}`;
    }
    case 'citizen-assessment': {
      const result = await moduleClientFactory.createCitizenAssessmentClient().getAssessmentById({ assessmentId: resourceId });
      return `/standalone/${citizenAreaPathName}/${result.assessment?.citizenId}/vum/${resourceId}`;
    }
    case 'citizen-assignment': {
      return joinURL(jdcBaseUrl, citizenAreaPathName, serviceProviderAreaPathName, rootEntityId || '', viewAssignmentPathName.replace(':assignmentId', resourceId));
    }
    case 'activities-assignment': {
      return `/standalone/${citizenAreaPathName}/${rootEntityId}/${assignmentPathName}/${resourceId}`;
    }
    case 'citizen-ruling': {
      return `/standalone/${citizenAreaPathName}/${rootEntityId}/ruling/${resourceId}`;
    }
    case 'citizen-clarification-right': {
      return `/standalone/${citizenAreaPathName}/${rootEntityId}/afklaringsret/${resourceId}`;
    }
    case 'citizen-ruling-continuation': {
      return `/standalone/${citizenAreaPathName}/${rootEntityId}/ruling/${resourceId}/continuation`;
    }
    case 'intervention-objective': {
      const response = await moduleClientFactory.createCitizenAssignmentClient().getAssignmentIdFromInterventionObjectiveId({ interventionObjectiveId: { value: resourceId } });
      const assignmentId = response.assignmentId?.value;
      if (!assignmentId) {
        throw new Error('Could not resolve assignment for intervention objective');
      }
      return `/standalone/${citizenAreaPathName}/${rootEntityId}/${assignmentPathName}/${assignmentId}/${interventionObjectivePathName}?interventionObjectiveId=${resourceId}`;
    }
    case 'serviceprovider-intervention-objective': {
      const response = await moduleClientFactory.createCitizenAssignmentClient().getAssignmentIdFromInterventionObjectiveId({ interventionObjectiveId: { value: resourceId } });
      const assignmentId = response.assignmentId?.value;
      if (!assignmentId) {
        throw new Error('Could not resolve assignment for intervention objective');
      }
      return `/${citizenAreaPathName}/${serviceProviderAreaPathName}/${rootEntityId}/view-assignment/${assignmentId}/${interventionObjectivePathName}?interventionObjectiveId=${resourceId}`;
    }
    case 'citizen-diarynote': {
      return joinURL(jdcBaseUrl, citizenAreaPathName, serviceProviderAreaPathName, rootEntityId || '', 'diary', journalNotePathName, journalNoteView, resourceId);
    }
    case 'follow-up-on-efforts': {
      const response = await moduleClientFactory.createCitizenAssignmentClient().getServiceProviderByPartialObjectiveId({ partialObjectiveId: { value: resourceId } });
      if (response?.serviceProviderUnitId) {
        return `/${citizenAreaPathName}/${serviceProviderAreaPathName}/${rootEntityId}/partial-objectives-observations?spuid=${response.serviceProviderUnitId}`;
      }

      throw new Error('Could not load partialobjective resource');
    }
    case 'serviceprovider-statusnote': {
      const response = await moduleClientFactory
        .createCitizenAssignmentClient()
        .getServiceProviderByStatusNoteId({ serviceProviderInterventionObjectiveStatusId: { value: resourceId } });
      if (response?.serviceProviderUnitId) {
        return `/${citizenAreaPathName}/${serviceProviderAreaPathName}/${rootEntityId}/${interventionObjectiveStatusPathName}?statusid=${resourceId}&spuId=${response.serviceProviderUnitId}`;
      }

      throw new Error('Could not load serviceprovider statusnote resource');
    }
    case 'authority-statusnote': {
      const response = await moduleClientFactory
        .createCitizenAssignmentClient()
        .getJournalNoteAttachmentIdByStatusNoteId({ serviceProviderInterventionObjectiveStatusId: { value: resourceId } });

      if (response?.journalNoteAttachmentId && rootEntityId) {
        const citizenpart = joinURL('/', 'standalone', citizenAreaPathName, rootEntityId, journalTabPathName, 'note', 'files');
        return `${citizenpart}/${response.journalNoteAttachmentId}`;
      }

      throw new Error('Could not load authority statusnote resource');
    }
    case 'citizenlesson': {
      // A worklist row for a citizen's lesson is keyed by the citizen lesson, while the attendance sheet
      // belongs to the lesson the whole class shares.
      const client = moduleClientFactory.createAttendanceClient();
      const response = await client.getCitizenLessonById({ citizenLessonId: resourceId });
      if (response?.citizenLesson?.lessonId) {
        return joinURL(jdcBaseUrl, attendanceAreaPathName, getLessonProtocolPath(response.citizenLesson.lessonId));
      }

      throw new Error('Could not load lesson for citizen lesson');
    }
    case 'offer-resource': {
      const client = moduleClientFactory.createOfferAndActivityClient();
      const response = await client.getOfferIdByOfferResourceId({ offerResourceId: resourceId });
      if (response?.offerId) {
        return joinURL(jdcBaseUrl, offerAndActivityAreaPathName, viewOfferPathName, response.offerId, 'offer-resource', 'view', resourceId);
      }

      throw new Error('Could not load offer resource without offerid');
    }
    case 'course-educator': {
      // A worklist row for an educator on a course is keyed by the link between them, while the page belongs to the educator.
      const client = moduleClientFactory.createAttendanceClient();
      const response = await client.getEducatorIdByCourseEducatorId({ courseEducatorId: resourceId });
      if (response?.educatorId) {
        return joinURL(jdcBaseUrl, attendanceAreaPathName, getEducatorEditPath(response.educatorId));
      }

      throw new Error('Could not load educator for course educator');
    }
    case 'lesson-educator': {
      const client = moduleClientFactory.createAttendanceClient();
      const response = await client.getEducatorIdByLessonEducatorId({ lessonEducatorId: resourceId });
      if (response?.educatorId) {
        return joinURL(jdcBaseUrl, attendanceAreaPathName, getEducatorEditPath(response.educatorId));
      }

      throw new Error('Could not load educator for lesson educator');
    }
    case 'waiting-list-seat': {
      const client = moduleClientFactory.createOfferAndActivityClient();
      const response = await client.getWaitingListSeatRoutingInfoById({ waitingListSeatId: resourceId });
      if (response?.offerId && response?.citizenId) {
        return joinURL(
          jdcBaseUrl,
          offerAndActivityAreaPathName,
          viewOfferPathName,
          response.offerId,
          waitingListSeatPathName,
          waitingListSeatEditPathName,
          response.citizenId,
          resourceId
        );
      }

      throw new Error('Could not load waiting list seat without offerId/citizenId');
    }
    case 'journalnoteattachmentview': {
      const journalClient = moduleClientFactory.createCitizenJournalClient();
      let citizenId = searchParams?.get('citizenId');
      if (!citizenId) {
        const citizenIdResponse = await journalClient.getCitizenIdByJournalNoteId({ journalNoteId: resourceId });
        citizenId = citizenIdResponse.citizenId;
      }
      if (!citizenId) {
        return joinURL(jdcBaseUrl, citizenJournalNote, resourceId);
      }
      const journalNoteResponse = await journalClient.getJournalNote({ journalNoteId: resourceId, citizenId });
      const attachment = journalNoteResponse.journalNote?.attachments?.[0];
      if (attachment?.documentIdentifier) {
        const citizenpart = joinURL('/', 'standalone', citizenAreaPathName, citizenId, journalTabPathName, 'note', 'files');
        return `${citizenpart}/${attachment.id}`;
      }
      return joinURL(jdcBaseUrl, 'standalone', citizenAreaPathName, citizenId, journalTabPathName, journalNotePathName, journalNoteView, resourceId);
    }
    default:
      return '';
  }
}

function parseNumberStr(str: string | undefined | null): number | undefined {
  if (str) {
    return Number(str);
  }
  return undefined;
}
